/**
 * User groups (`users/groups`) and an internal user's membership in them
 * (`users/userList/{uid}/groups`, a list of group keys).
 *
 * Membership is only a navigation convenience in the iOS Service and inventory
 * views: a user with groups sees the buildings whose `responsibleGroups`
 * overlap theirs. Groups are read-only here; membership is written through the
 * admin callable `updateUserGroups`, since the field is not client-writable.
 */
import { get, getDatabase, ref } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { recordActivity } from './activityApi';

export interface UserGroup {
    key: string;
    name: string;
}

export async function fetchUserGroups(): Promise<UserGroup[]> {
    const snapshot = await get(ref(getDatabase(), 'users/groups'));
    if (!snapshot.exists()) return [];
    const data = snapshot.val() as Record<string, { name?: string }>;
    return Object.entries(data)
        .map(([key, value]) => ({ key, name: value?.name || key }))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
}

export async function updateUserGroups(uid: string, groups: string[]): Promise<void> {
    const fn = httpsCallable<{ uid: string; groups: string[] }, { success: boolean }>(functions, 'updateUserGroups');
    await fn({ uid, groups });
    recordActivity('user.updated', { targetKey: uid });
}
